import React, { useState, useCallback, useMemo } from "react";
import { getWalletTransactions } from "../../services/api";
import { useApiQuery } from "../../hooks/useApiQuery";
import { format } from "date-fns";

const WalletTransactions = () => {
  const [filters, setFilters] = useState({
    userId: "",
    type: "",
    startDate: "",
    endDate: "",
  });

  // Frontend-only search. Does not trigger API requests.
  const [searchInput, setSearchInput] = useState("");

  const [page, setPage] = useState(1);
  const limit = 20;

  // Exclude searchInput from the API query key.
  // Only type/date filters and pagination trigger API requests.
  const queryKey = useMemo(() => {
    return [
      "wallet-transactions",
      {
        type: filters.type,
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
      page,
    ];
  }, [filters.type, filters.startDate, filters.endDate, page]);

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useApiQuery(
    queryKey,
    () => {
      const params = {
        type: filters.type || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        userOnly: "true",
        page,
        limit,
      };

      return getWalletTransactions(params).then((res) => res.data);
    },
    {
      staleTime: 30 * 1000,
      keepPreviousData: true,
      onError: (error) => {
        console.error("Failed to fetch transactions:", error);
      },
    },
  );

  const transactions = response?.transactions || [];

  const pagination = {
    page: response?.pagination?.page || 1,
    total: response?.pagination?.total || 0,
    totalPages: response?.pagination?.totalPages || 0,
  };

  // Instant frontend search across the currently loaded transactions.
  const filteredTransactions = useMemo(() => {
    const search = searchInput.trim().toLowerCase();

    if (!search) {
      return transactions;
    }

    return transactions.filter((t) => {
      const userName =
        typeof t.userId === "object" && t.userId !== null
          ? t.userId.name || ""
          : "";

      const userId =
        typeof t.userId === "object" && t.userId !== null
          ? t.userId._id || ""
          : t.userId || "";

      const userEmail =
        typeof t.userId === "object" && t.userId !== null
          ? t.userId.email || ""
          : "";

      const searchableFields = [
        userName,
        userId,
        userEmail,
        t.description,
        t.type,
        t.amount,
      ];

      return searchableFields.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(search),
      );
    });
  }, [transactions, searchInput]);

  // Totals are calculated from the currently visible results.
  const { totalCredit, totalDebit } = useMemo(() => {
    let credit = 0;
    let debit = 0;

    filteredTransactions.forEach((t) => {
      const amount = Number(t.amount) || 0;

      if (t.type === "credit") credit += amount;
      else if (t.type === "debit") debit += amount;
    });

    return {
      totalCredit: credit,
      totalDebit: debit,
    };
  }, [filteredTransactions]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const fmtINR = (amount) =>
    `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading transactions...</p>
        </div>
      </div>
    );
  }

  if (isError && !response) {
    return (
      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6">
        <div className="text-center py-8 text-[#708090]">
          <p className="text-red-500">Failed to load transactions</p>

          <button
            onClick={handleRefresh}
            className="mt-2 text-[#1471b9] hover:underline"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-4 items-center">
        <input
          name="search"
          placeholder="Search  transactions..."
          value={searchInput}
          onChange={handleSearchChange}
          className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
        />

        <select
          name="type"
          value={filters.type}
          onChange={handleFilterChange}
          className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
        >
          <option value="">All Transactions</option>
          <option value="credit">Credit </option>
          <option value="debit">Debit </option>
        </select>

        <input
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleFilterChange}
          className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
        />

        <input
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleFilterChange}
          className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
        />

        
<button
  type="button"
  onClick={() => {
    setFilters((prev) => ({
      ...prev,
      startDate: '',
      endDate: '',
    }));
    setPage(1);
  }}
  disabled={!filters.startDate && !filters.endDate}
  className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm text-[#708090] bg-transparent hover:bg-transparent disabled:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
>
  Clear Dates
</button>


        {isFetching && (
          <span className="text-sm text-[#708090] animate-pulse">
            Refreshing...
          </span>
        )}
      </div>

      {/* Transactions table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#dae1e7]">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-[#708090] uppercase">
                User
              </th>

              <th className="px-4 py-2 text-left text-xs font-semibold text-[#708090] uppercase">
                Amount
              </th>

              <th className="px-4 py-2 text-left text-xs font-semibold text-[#708090] uppercase">
                Type
              </th>

              

              <th className="px-4 py-2 text-left text-xs font-semibold text-[#708090] uppercase">
                Date
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-[#dae1e7]">
            {filteredTransactions.length === 0 ? (
              <tr>
                <td
                  colSpan="4"
                  className="px-4 py-6 text-center text-[#708090]"
                >
                  {searchInput.trim()
                    ? "No matching transactions found"
                    : "No transactions found"}
                </td>
              </tr>
            ) : (
              filteredTransactions.map((t) => (
                <tr
                  key={t._id}
                  className="hover:bg-[#f8f9fa] transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-[#2b3445]">
                    {t.userId?.name || t.userId || "-"}
                  </td>

                  <td
                    className={`px-4 py-3 text-sm font-semibold ${
                      t.type === "credit" ? "text-green-600" : "text-red-500"
                    }`}
                  >
                    {t.type === "credit" ? "+" : "-"}
                    {fmtINR(t.amount)}
                  </td>

                  <td className="px-4 py-3 text-sm capitalize text-[#708090]">
                    {t.type}
                  </td>


                  <td className="px-4 py-3 text-sm text-[#708090]">
                    {t.createdAt
                      ? format(new Date(t.createdAt), "dd MMM yyyy, HH:mm")
                      : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-end gap-2 mt-4">
          <button
            disabled={pagination.page === 1 || isFetching}
            onClick={() => handlePageChange(pagination.page - 1)}
            className="px-3 py-1 border border-[#dae1e7] rounded-[8px] text-sm disabled:opacity-50 hover:bg-[#f6f9fc] transition-colors"
          >
            Previous
          </button>

          <span className="px-3 py-1 text-sm text-[#708090]">
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            disabled={pagination.page === pagination.totalPages || isFetching}
            onClick={() => handlePageChange(pagination.page + 1)}
            className="px-3 py-1 border border-[#dae1e7] rounded-[8px] text-sm disabled:opacity-50 hover:bg-[#f6f9fc] transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default WalletTransactions;
